import { Button } from '../../../../../widgets/buttons';
import { Dialog, useDialogManager } from '../../../../../widgets/dialog';
import { Input } from '../../../../../widgets/input';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../../../widgets/notificationToaster';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { commitMutation, fetchQuery, useLazyLoadQuery } from 'react-relay/hooks';
import environment from 'src/relay/environment';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { deleteLibraryMaterialMutation } from './query/deleteLibraryMaterialMutation';
import { libraryMaterialQuery } from './query/libraryMaterialQuery';
import { libraryMaterialsQuery } from './query/materialsLibraryQuery';

const ImportDialogWindow = styled(Dialog)`
    max-width: 800px;
    max-height: 600px;
`;

const MaterialsListContainer = styled.div`
    border: 1px ${(props) => props.theme.app.primary_light} solid;
    height: 400px;
    border-radius: 3px;
    overflow: auto;
    padding: 10px;
    margin: 10px 0;
`;

const MaterialItem = styled.div<{ $selected: boolean }>`
    display: flex;
    align-items: center;
    padding: 10px;
    margin: 5px 0;
    border-radius: 5px;
    cursor: pointer;
    background-color: ${(props) => (props.$selected ? props.theme.app.primary_light : 'transparent')};
    border: 1px solid ${(props) => (props.$selected ? props.theme.app.primary : 'transparent')};

    &:hover {
        background-color: ${(props) => props.theme.app.primary_light};
    }
`;

const MaterialPreviewImage = styled.img`
    width: 40px;
    height: 40px;
    border-radius: 3px;
    margin-right: 15px;
    object-fit: cover;
    background-color: ${(props) => props.theme.app.primary_light};
`;

const MaterialName = styled.span`
    font-size: 14px;
    color: ${(props) => props.theme.input.color};
`;

const ButtonsContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
`;

const SelectionCounter = styled.div`
    margin: 10px 0;
    font-size: 12px;
    color: ${(props) => props.theme.input.color};
`;

const SearchContainer = styled.div`
    margin: 10px 0;
`;

const SearchInput = styled(Input)`
    width: 100%;
    padding: 8px;
    border: 1px solid ${(props) => props.theme.app.primary_light};
    border-radius: 3px;
    font-size: 14px;
`;

interface LibraryMaterial {
    id: string;
    materialID: string;
    tempMaterialID: string;
    name: string;
    json: string;
    url: string;
    rootUrl: string;
    materialFileName: string;
    sellerID: number;
    imageUrl: string | null;
    imageData: string | null;
    hasShaders: boolean;
    sourceTextureImages: Array<{
        sceneID: string;
        sourceID: string;
        materialID: string;
        url: string;
        fileLength: number;
    }>;
    textureImages: Array<{
        textureImageID: string;
        imageUrl: string;
        imageFileLength: number;
    }>;
    shaders: {
        materialID: string;
        name: string;
        vertex: string;
        fragment: string;
    } | null;
    shaderInputs: Array<{
        inputID: string;
        name: string;
        shaderInputTypeID: number;
    }>;
    shaderOptions: Array<{
        optionID: string;
        name: string;
        shaderOptionTypeID: number;
    }>;
}

interface MaterialImportDialogProps {
    sceneEditor?: any;
    onImport: (selectedMaterials: LibraryMaterial[]) => void;
    onDelete?: (selectedMaterials: LibraryMaterial[]) => void;
    onMaterialsUpdated?: (materials: any[]) => void;
}

const MaterialImportDialog: FC<MaterialImportDialogProps> = ({ sceneEditor, onImport, onDelete, onMaterialsUpdated }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [selectedMaterials, setSelectedMaterials] = useState<Set<string>>(new Set());
    const [isImporting, setIsImporting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshKey, setRefreshKey] = useState(0); // Per forzare il refresh della query
    const [deletedMaterialIds, setDeletedMaterialIds] = useState<Set<string>>(new Set());

    // Aggiorniamo quando onMaterialsUpdated viene chiamato
    useEffect(() => {
        // Forza un re-render quando vengono aggiornati i materiali
        // Questo forza Relay a rifare la query
        setSelectedMaterials(new Set());
    }, [onMaterialsUpdated]);

    // Query per ottenere i materiali della libreria - con refreshKey per forzare refresh
    const data = useLazyLoadQuery<any>(libraryMaterialsQuery, { refreshKey }, { fetchPolicy: 'network-only' });
    const libraryMaterials: LibraryMaterial[] = data.libraryMaterials || [];

    // Filtra i materiali basandosi sulla ricerca
    const filteredMaterials = useMemo(() => {
        let materials = libraryMaterials.filter((material) => !deletedMaterialIds.has(material.materialID));

        if (!searchQuery.trim()) {
            return materials;
        }

        const query = searchQuery.toLowerCase().trim();
        return materials.filter(
            (material: LibraryMaterial) => material.name.toLowerCase().includes(query) || material.materialID.toLowerCase().includes(query)
        );
    }, [libraryMaterials, searchQuery, deletedMaterialIds]);

    const handleMaterialClick = useCallback((materialId: string) => {
        console.log('🖱️ Material clicked:', materialId);
        setSelectedMaterials((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(materialId)) {
                console.log('🔄 Deselecting material:', materialId);
                newSet.delete(materialId);
            } else {
                console.log('✅ Selecting material:', materialId);
                newSet.add(materialId);
            }
            console.log('📋 Selected materials now:', Array.from(newSet));
            return newSet;
        });
    }, []);

    const handleImport = useCallback(async () => {
        const selectedMaterialsData = filteredMaterials.filter((material: LibraryMaterial) => selectedMaterials.has(material.id));

        setIsImporting(true);

        if (sceneEditor && sceneEditor.importLibraryMaterial) {
            console.log(
                '🔄 Starting import process for materials:',
                selectedMaterialsData.map((m) => m.name)
            );

            // Prima recupera i dettagli completi di ogni materiale selezionato via GraphQL
            const detailedMaterials = await Promise.all(
                selectedMaterialsData.map(async (material) => {
                    console.log('📡 Fetching detailed data for material:', material.name, 'ID:', material.materialID);

                    const detailedData: any = await fetchQuery(environment, libraryMaterialQuery, { libraryMaterialId: material.materialID }).toPromise();

                    console.log('✅ Detailed data retrieved for:', material.name);
                    return detailedData.libraryMaterial;
                })
            );

            // Poi per ognuno trasforma i dati nel formato corretto e importa
            detailedMaterials.forEach((detailedMaterial: any, index: number) => {
                if (detailedMaterial) {
                    console.log(`🔄 [${index + 1}/${detailedMaterials.length}] Importing library material:`, detailedMaterial.name);
                    console.log('sono detailedMaterial raw data:', detailedMaterial);

                    // Crea Material usando il constructor diretto - il più semplice!
                    const zkMaterial = new (window as any).Zakeke.Material(detailedMaterial);
                    console.log('✅ Constructor with full object succeeded:', zkMaterial);

                    sceneEditor.importLibraryMaterial(zkMaterial);
                    console.log(`✅ [${index + 1}/${detailedMaterials.length}] Successfully imported:`, detailedMaterial.name);
                }
            });

            console.log('✅ All import operations completed');

            // PATTERN OBJECTEDITOR: dopo l'import, aggiorna i materiali come fa prepareModel
            const updatedMaterials = sceneEditor.getAllMaterials();
            console.log('📊 Updated materials from SceneEditor after import:', updatedMaterials.length);

            if (onMaterialsUpdated) {
                console.log('sono if');
                onMaterialsUpdated(updatedMaterials);
            }

            // Chiama la callback passando i materiali con dettagli completi
            onImport(detailedMaterials.filter(Boolean));
        } else {
            console.warn('⚠️ sceneEditor.importLibraryMaterial not available, using fallback');
            onImport(selectedMaterialsData);
        }

        setIsImporting(false);
        // Non chiamiamo più closeDialog qui - è gestito dal chiamante
    }, [selectedMaterials, filteredMaterials, sceneEditor, onImport, onMaterialsUpdated]);

    const handleDelete = useCallback(async () => {
        console.log('🗑️ Delete button clicked');
        const selectedMaterialsData = filteredMaterials.filter((material: LibraryMaterial) => selectedMaterials.has(material.id));

        if (selectedMaterialsData.length === 0) return;

        // Mostra dialog di conferma invece di window.confirm
        showDialog(
            'delete-confirmation-dialog',
            <Dialog onClose={() => closeDialog('delete-confirmation-dialog')}>
                <div style={{ padding: '20px' }}>
                    <h4>{T._('Confirm Delete', 'Admin')}</h4>
                    <p>{T._(`Are you sure you want to delete ${selectedMaterialsData.length} material(s) from the library?`, 'Admin')}</p>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <Button onClick={() => closeDialog('delete-confirmation-dialog')}>{T._('Cancel', 'Admin')}</Button>
                        <Button
                            onClick={async () => {
                                closeDialog('delete-confirmation-dialog');

                                // Mostra notifica "deleting selected library materials"
                                const deleteNotificationId = showInfoNotification({
                                    message: T._('Deleting selected library materials...', 'Admin')
                                });

                                try {
                                    console.log(
                                        '�️ Starting delete process for materials:',
                                        selectedMaterialsData.map((m) => m.name)
                                    );

                                    // Elimina ogni materiale usando la mutation GraphQL
                                    for (const material of selectedMaterialsData) {
                                        console.log(`🗑️ Deleting material: ${material.name} (ID: ${material.materialID})`);

                                        await new Promise<void>((resolve, reject) => {
                                            commitMutation(environment, {
                                                mutation: deleteLibraryMaterialMutation,
                                                variables: {
                                                    deleteLibraryMaterialId: material.materialID
                                                },
                                                onCompleted: (response: any) => {
                                                    console.log(`✅ Successfully deleted material: ${material.name}`, response);

                                                    // Aggiorna immediatamente lo stato locale
                                                    setDeletedMaterialIds((prev) => new Set([...prev, material.materialID]));

                                                    resolve();
                                                },
                                                onError: (error: any) => {
                                                    console.error(`❌ Error deleting material ${material.name}:`, error);
                                                    reject(error);
                                                }
                                            });
                                        });
                                    }

                                    // Aggiorna la lista dei materiali dopo l'eliminazione
                                    console.log('Updating materials list after delete...');

                                    // Rimuovi la notifica di progresso
                                    removeNotification(deleteNotificationId);

                                    // Forza il refresh della lista aggiornando il refreshKey
                                    setRefreshKey((prev) => prev + 1);

                                    // Reset selezione
                                    setSelectedMaterials(new Set());

                                    console.log('Delete process completed successfully');

                                    // Mostra notification di successo
                                    showSuccessNotification({
                                        message: T._('Deleted selected library materials', 'Admin')
                                    });
                                } catch (error) {
                                    // Rimuovi la notifica di progresso
                                    removeNotification(deleteNotificationId);

                                    console.error('Error during delete process:', error);
                                    showErrorNotification({
                                        message: T._('Error deleting materials: ' + (error as Error).message, 'Admin')
                                    });
                                }
                            }}
                        >
                            {T._('Delete', 'Admin')}
                        </Button>
                    </div>
                </div>
            </Dialog>
        );
    }, [selectedMaterials, filteredMaterials]);

    return (
        <ImportDialogWindow onClose={() => closeDialog('import-materials')}>
            <h3>{T._('Import Materials from Library', 'Admin')}</h3>

            <SearchContainer>
                <SearchInput
                    type='text'
                    placeholder={T._('Search materials...', 'Admin')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </SearchContainer>

            <SelectionCounter>
                {T._('Selected materials:', 'Admin')} {selectedMaterials.size} / {filteredMaterials.length}
            </SelectionCounter>

            <MaterialsListContainer>
                {filteredMaterials.map((material: LibraryMaterial) => (
                    <MaterialItem key={material.id} $selected={selectedMaterials.has(material.id)} onClick={() => handleMaterialClick(material.id)}>
                        <MaterialPreviewImage
                            src={material.imageUrl || '/assets/images/others/product-configurator.png'}
                            alt={material.name}
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = '/assets/images/others/product-configurator.png';
                            }}
                        />
                        <MaterialName title={`Library Name: ${material.name} | ID: ${material.materialID}`}>{material.name}</MaterialName>
                    </MaterialItem>
                ))}
            </MaterialsListContainer>

            <ButtonsContainer>
                <Button onClick={handleDelete} disabled={selectedMaterials.size === 0 || isImporting}>
                    {T._('Delete Selected', 'Admin')}
                </Button>
                <Button onClick={() => closeDialog('import-materials')} disabled={isImporting}>
                    {T._('Cancel', 'Admin')}
                </Button>
                <Button onClick={handleImport} disabled={selectedMaterials.size === 0 || isImporting}>
                    {isImporting ? T._('Importing...', 'Admin') : T._('Import Selected', 'Admin')}
                </Button>
            </ButtonsContainer>
        </ImportDialogWindow>
    );
};

export default MaterialImportDialog;
